import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
    GetTargetAndWIPVolumeRequest,
    GetTargetAndWIPVolumeResponse,
    ProcessPathOverrides, VerifyVolumeResponse,
    VolumeDistribution
} from "../../pages/volumesAndWip/model";
import { getTargetAndWIPVolumes, verifyVolumes } from "../../pages/volumesAndWip/api";
import { showError } from "./errorSlice";
import { RootState } from "../types";

// Interface for process path override data (keeping for backward compatibility during transition)
export interface ProcessPathOverride {
  override?: number;
  wipOverride?: number;
  overrideReason?: string;
  overrideComment?: string;
  wipOverrideReason?: string;
  wipOverrideComment?: string;
}

export interface TrailerOverrideData {
    trailerId: string;
    priority: number;
    originalPriority?: number;
    volume: number;
    reorderReason?: string;
    reorderComment?: string;
    volumeOverrideReason?: string;
    volumeOverrideComment?: string;
}

export interface VolumesAndWIPState {
    targetAndWIPVolume?: GetTargetAndWIPVolumeResponse;
    verifyVolumeResponse?: VerifyVolumeResponse;
    processPathOverrides: ProcessPathOverrides; // New format
    trailerOverrides: TrailerOverrideData[]; // Inbound trailer overrides
    loading: boolean;
    error: string | null;
}

const initialState: VolumesAndWIPState = {
    targetAndWIPVolume: undefined,
    verifyVolumeResponse: undefined,
    processPathOverrides: {
        volumeContext: {
            secondaryPathVolumeData: {
                wipSecondaryVolumeDistribution: [],
                targetPrimaryVolumeDistribution: [],
                targetSecondaryVolumeDistribution: []
            }
        }
    },
    trailerOverrides: [],
    loading: false,
    error: null,
}

// Payload interfaces for actions - updated for new structure
export interface UpdateVolumeDistributionPayload {
  operationName: string;
  volume: number;
  distributionType: 'wipSecondaryVolumeDistribution' | 'targetPrimaryVolumeDistribution' | 'targetSecondaryVolumeDistribution';
}

export interface RemoveVolumeDistributionPayload {
  operationName: string;
  distributionType: 'wipSecondaryVolumeDistribution' | 'targetPrimaryVolumeDistribution' | 'targetSecondaryVolumeDistribution';
}

// TypeScript interfaces for the new productivity rates context
export interface FunctionProductivity {
  actualProductivity: number;
  overrideReason?: string;
  overrideComment?: string;
}

export interface FunctionReport {
  processPath: string;
  functionId: string;
  functionName: string;
  functionType: string;
  functionProductivity: FunctionProductivity;
}

export interface ProductivityRatesContext {
  functionReports: FunctionReport[];
}

// Keep old interfaces for backward compatibility during transition
export interface UpdateProcessPathOverridePayload {
  processPathName: string;
  field: 'override' | 'wipOverride' | 'overrideReason' | 'overrideComment' | 'wipOverrideReason' | 'wipOverrideComment';
  value: number | string | undefined;
}

export interface SaveProcessPathOverridePayload {
  processPathName: string;
  override?: number;
  wipOverride?: number;
  overrideReason?: string;
  overrideComment?: string;
}

export const getTargetAndWipVolumeThunk = createAsyncThunk<GetTargetAndWIPVolumeResponse, GetTargetAndWIPVolumeRequest>(
    'volumesAndWip/getTargetAndWIPVolume',
    async (getTargetAndWIPVolumeRequest: GetTargetAndWIPVolumeRequest, { dispatch, rejectWithValue }) => {
        try {
            console.log("Received call for getTargetAndWIPVolume for request: ", getTargetAndWIPVolumeRequest);
            const response = await getTargetAndWIPVolumes(getTargetAndWIPVolumeRequest);
            console.log("GetTargetAndWIPVolumeResponse: ", response);
            return response;
        } catch (error: any) {
            console.error("Error fetching Target and WIP volume from API:", error);
            // Error modal is already dispatched by the API layer (handleGetTargetAndWIPVolumesError)
            // with the backend error message. No need to dispatch a duplicate showError here.
            return rejectWithValue(error);
        }
    }
);

export const verifyVolumesThunk = createAsyncThunk(
    'volumesAndWip/verifyVolumes',
    async (params: { planId: string; processPathOverrides?: ProcessPathOverrides }, {dispatch, getState, rejectWithValue}) => {
        try {
            console.log("Received call for verifyVolume for params: {}", params);
            // Access rosterAndRates data from Redux store
            const state = getState() as RootState;
            const { totalAttendanceOverride, rateOverrides, categoryOverrides } = state.rosterAndRates;
            
            // Build functionReports array from rateOverrides (not categoryOverrides)
            const functionReports: FunctionReport[] = Object.entries(rateOverrides).map(([functionId, override]) => ({
                processPath: override.processPath || "",
                functionId: override.functionId || functionId,
                functionName: override.functionName || "",
                functionType: override.functionType || "",
                functionProductivity: {
                    actualProductivity: override.override || 0,
                    overrideReason: override.overrideReason,
                    overrideComment: override.overrideComment
                }
            }));
            
            // Build productivityRatesContext object
            const productivityRatesContext: ProductivityRatesContext = {
                functionReports
            };
            
            // Build rosterData array from categoryOverrides
            const rosterData = Object.entries(categoryOverrides).map(([categoryName, categoryOverride]) => ({
                propertyName: categoryName,
                overrideValue: categoryOverride.override || 0,
                overrideReason: categoryOverride.overrideReason,
                overrideComment: categoryOverride.overrideComment
            }));
            
            // Get existing processPathOverrides (volumeContext) or use default
            const volumeContext = params.processPathOverrides || {
                volumeContext: {
                    secondaryPathVolumeData: {
                        wipSecondaryVolumeDistribution: [],
                        targetPrimaryVolumeDistribution: [],
                        targetSecondaryVolumeDistribution: []
                    }
                }
            };
            
            // Get trailer overrides from Redux
            const trailerOverrides = state.volumesAndWip.trailerOverrides;

            // Combine all contexts in a single object within an array format as required
            const combinedInputOverrides = [
                {
                    volumeContext: volumeContext.volumeContext,
                    productivityRatesContext,
                    totalAttendanceOverride: totalAttendanceOverride,
                    rosterData,
                    trailerOverrides: trailerOverrides.length > 0 ? trailerOverrides : undefined
                }
            ];
            
            const response = await verifyVolumes(params.planId, combinedInputOverrides);
            console.log("VerifyVolume Response: ", response);
            return response;
        } catch (error) {
            console.error("Error verifying volumes:", error);
            dispatch(showError({
                title: "Failed to Verify Volumes",
                message: "Unable to verify volumes data. Please try again later."
            }));
            return rejectWithValue(error);
        }
    }
)

const volumesAndWipSlice = createSlice({
    name: 'volumesAndWip',
    initialState,
    reducers: {
        // Add or update volume distribution
        updateVolumeDistribution: (state, action: PayloadAction<UpdateVolumeDistributionPayload>) => {
            const { operationName, volume, distributionType } = action.payload;
            const distributions = state.processPathOverrides.volumeContext.secondaryPathVolumeData[distributionType];
            
            // Find existing distribution or create new one
            const existingIndex = distributions.findIndex(dist => dist.operationName === operationName);
            
            if (existingIndex >= 0) {
                distributions[existingIndex].volume = volume;
            } else {
                distributions.push({ operationName, volume });
            }
        },

        // Remove volume distribution
        removeVolumeDistribution: (state, action: PayloadAction<RemoveVolumeDistributionPayload>) => {
            const { operationName, distributionType } = action.payload;
            const distributions = state.processPathOverrides.volumeContext.secondaryPathVolumeData[distributionType];
            
            const index = distributions.findIndex(dist => dist.operationName === operationName);
            if (index >= 0) {
                distributions.splice(index, 1);
            }
        },

        // Clear all overrides (reset to initial state)
        clearAllProcessPathOverrides: (state) => {
            state.processPathOverrides = {
                volumeContext: {
                    secondaryPathVolumeData: {
                        wipSecondaryVolumeDistribution: [],
                        targetPrimaryVolumeDistribution: [],
                        targetSecondaryVolumeDistribution: []
                    }
                }
            };
        },

        // Set complete override data
        setProcessPathOverrides: (state, action: PayloadAction<ProcessPathOverrides>) => {
            state.processPathOverrides = action.payload;
        },

        // Legacy actions for backward compatibility during transition - will map to new format
        updateProcessPathOverride: (state, action: PayloadAction<UpdateProcessPathOverridePayload>) => {
            const { processPathName, field, value } = action.payload;
            
            // Determine if this process path is primary or secondary by checking targetAndWIPVolume
            const isPrimary = state.targetAndWIPVolume?.primaryTargetAndWipVolume?.some(
                volume => volume.label === processPathName
            ) || false;
            
            // Map old format to new format based on field type and context
            if (field === 'override') {
                // Target volume override - goes to primary or secondary distribution based on context
                const distributionType = isPrimary ? 'targetPrimaryVolumeDistribution' : 'targetSecondaryVolumeDistribution';
                const distributions = state.processPathOverrides.volumeContext.secondaryPathVolumeData[distributionType];
                const existingIndex = distributions.findIndex(dist => dist.operationName === processPathName);
                
                if (value !== undefined && value !== null) {
                    if (existingIndex >= 0) {
                        distributions[existingIndex].volume = value as number;
                    } else {
                        distributions.push({ operationName: processPathName, volume: value as number });
                    }
                } else if (existingIndex >= 0) {
                    distributions.splice(existingIndex, 1);
                }
            } else if (field === 'wipOverride') {
                // WIP override - always goes to wipSecondaryVolumeDistribution (only secondary volumes have WIP)
                const distributions = state.processPathOverrides.volumeContext.secondaryPathVolumeData.wipSecondaryVolumeDistribution;
                const existingIndex = distributions.findIndex(dist => dist.operationName === processPathName);
                
                if (value !== undefined && value !== null) {
                    if (existingIndex >= 0) {
                        distributions[existingIndex].volume = value as number;
                    } else {
                        distributions.push({ operationName: processPathName, volume: value as number });
                    }
                } else if (existingIndex >= 0) {
                    distributions.splice(existingIndex, 1);
                }
            } else if (field === 'overrideReason' || field === 'overrideComment') {
                // Volume override reason/comment - goes to primary or secondary distribution based on context
                const distributionType = isPrimary ? 'targetPrimaryVolumeDistribution' : 'targetSecondaryVolumeDistribution';
                const distributions = state.processPathOverrides.volumeContext.secondaryPathVolumeData[distributionType];
                const existingIndex = distributions.findIndex(dist => dist.operationName === processPathName);
                
                // Only save reason/comment if there's already a volume override
                if (existingIndex >= 0) {
                    const fieldName = field === 'overrideReason' ? 'overrideReason' : 'overrideComment';
                    if (value !== undefined && value !== null && value !== '') {
                        distributions[existingIndex][fieldName] = value as string;
                    } else {
                        delete distributions[existingIndex][fieldName];
                    }
                }
            } else if (field === 'wipOverrideReason' || field === 'wipOverrideComment') {
                // WIP override reason/comment - always goes to wipSecondaryVolumeDistribution
                const distributions = state.processPathOverrides.volumeContext.secondaryPathVolumeData.wipSecondaryVolumeDistribution;
                const existingIndex = distributions.findIndex(dist => dist.operationName === processPathName);
                
                // Only save reason/comment if there's already a WIP override
                if (existingIndex >= 0) {
                    const fieldName = field === 'wipOverrideReason' ? 'overrideReason' : 'overrideComment';
                    if (value !== undefined && value !== null && value !== '') {
                        distributions[existingIndex][fieldName] = value as string;
                    } else {
                        delete distributions[existingIndex][fieldName];
                    }
                }
            }
        },

        // Set loading state
        setVolumesLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },

        // Set trailer overrides (from inbound trailer page)
        setTrailerOverrides: (state, action: PayloadAction<TrailerOverrideData[]>) => {
            state.trailerOverrides = action.payload;
        },

        // Set error state
        setVolumesError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getTargetAndWipVolumeThunk.fulfilled, (state, action) => {
                state.targetAndWIPVolume = action.payload;
                // Initialize volume/WIP overrides from backend if they return override data
                // and Redux doesn't already have overrides (e.g., after page refresh)
                const hasExistingOverrides =
                    state.processPathOverrides.volumeContext.secondaryPathVolumeData.targetPrimaryVolumeDistribution.length > 0 ||
                    state.processPathOverrides.volumeContext.secondaryPathVolumeData.targetSecondaryVolumeDistribution.length > 0 ||
                    state.processPathOverrides.volumeContext.secondaryPathVolumeData.wipSecondaryVolumeDistribution.length > 0;

                if (!hasExistingOverrides) {
                    // Initialize from primary volumes if backend returns override fields
                    action.payload.primaryTargetAndWipVolume?.forEach((vol) => {
                        const volRecord = vol as unknown as Record<string, unknown>;
                        const overrideVolume = volRecord.overrideVolume as number | undefined;
                        if (overrideVolume !== undefined && overrideVolume !== null) {
                            state.processPathOverrides.volumeContext.secondaryPathVolumeData.targetPrimaryVolumeDistribution.push({
                                operationName: vol.label,
                                volume: overrideVolume,
                                overrideReason: volRecord.overrideReason as string | undefined,
                                overrideComment: volRecord.overrideComment as string | undefined,
                            });
                        }
                    });

                    // Initialize from secondary volumes if backend returns override fields
                    action.payload.secondaryTargetAndWipVolume?.forEach((vol) => {
                        const volRecord = vol as unknown as Record<string, unknown>;
                        const overrideVolume = volRecord.overrideVolume as number | undefined;
                        if (overrideVolume !== undefined && overrideVolume !== null) {
                            state.processPathOverrides.volumeContext.secondaryPathVolumeData.targetSecondaryVolumeDistribution.push({
                                operationName: vol.label,
                                volume: overrideVolume,
                                overrideReason: volRecord.overrideReason as string | undefined,
                                overrideComment: volRecord.overrideComment as string | undefined,
                            });
                        }
                        const overrideWipVolume = volRecord.overrideWipVolume as number | undefined;
                        if (overrideWipVolume !== undefined && overrideWipVolume !== null) {
                            state.processPathOverrides.volumeContext.secondaryPathVolumeData.wipSecondaryVolumeDistribution.push({
                                operationName: vol.label,
                                volume: overrideWipVolume,
                                overrideReason: volRecord.wipOverrideReason as string | undefined,
                                overrideComment: volRecord.wipOverrideComment as string | undefined,
                            });
                        }
                    });
                }
            })
            .addCase(getTargetAndWipVolumeThunk.rejected, (state, action) => {
                state.targetAndWIPVolume = undefined;
            })
            .addCase(verifyVolumesThunk.fulfilled, (state, action) => {
                state.verifyVolumeResponse = action.payload;
            })
            .addCase(verifyVolumesThunk.rejected, (state, action) => {
                state.verifyVolumeResponse = undefined;
            })
    }
});

// Export actions
export const {
    updateVolumeDistribution,
    removeVolumeDistribution,
    clearAllProcessPathOverrides,
    setProcessPathOverrides,
    updateProcessPathOverride, // Legacy action for backward compatibility
    setTrailerOverrides,
    setVolumesLoading,
    setVolumesError,
} = volumesAndWipSlice.actions;

export default volumesAndWipSlice.reducer;
